import React from "react"

const CHART_HEIGHT = 120
const VIEW_WIDTH = 1000
const TOP_PAD = 16
const BOTTOM_PAD = 12

function monthDay(iso) {
  const d = new Date(iso + "T00:00:00")
  const month = d.toLocaleString("en-US", { month: "short" }).toUpperCase()
  return `${month} ${String(d.getDate()).padStart(2, "0")}`
}

function buildPath(points) {
  if (points.length === 0) return ""
  return points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(" ")
}

function buildAreaPath(points) {
  if (points.length === 0) return ""
  const line = buildPath(points)
  const lastX = points[points.length - 1].x.toFixed(2)
  const firstX = points[0].x.toFixed(2)
  return `${line} L${lastX},${CHART_HEIGHT} L${firstX},${CHART_HEIGHT} Z`
}

function EmptyState({ t }) {
  return (
    <div
      className="bg-surface-card border border-rule rounded-card-lg p-10 flex items-center justify-center"
      style={{ height: 160 }}
    >
      <p className="text-sm text-ink-tertiary text-center max-w-md">
        {t("dashboard.overview.chart_empty")}
      </p>
    </div>
  )
}

export default function HeroChart({ t, aggregates }) {
  if (!aggregates || !aggregates.sufficient_data) {
    return <EmptyState t={t} />
  }

  const series = aggregates.series || []
  const winners = aggregates.winners || []
  if (series.length === 0) return <EmptyState t={t} />

  const rates = series.map((row) => row.conversion_rate || 0)
  const maxRate = Math.max(...rates, 0.5)
  const yFor = (rate) => {
    const usable = CHART_HEIGHT - TOP_PAD - BOTTOM_PAD
    return TOP_PAD + (1 - rate / maxRate) * usable
  }
  const xFor = (i) => (series.length === 1 ? 0 : (i / (series.length - 1)) * VIEW_WIDTH)

  const points = series.map((row, i) => ({ x: xFor(i), y: yFor(row.conversion_rate || 0), row }))
  const current = points[points.length - 1]

  const winnerPoints = winners
    .map((w) => {
      const idx = series.findIndex((row) => row.date === w.date)
      return idx >= 0 ? { ...w, x: xFor(idx), y: yFor(series[idx].conversion_rate || 0) } : null
    })
    .filter(Boolean)

  const axisIndices = [
    0,
    Math.floor(series.length / 4),
    Math.floor(series.length / 2),
    Math.floor((3 * series.length) / 4),
    series.length - 1
  ]

  return (
    <div className="bg-surface-card border border-rule rounded-card-lg p-6">
      <svg
        width="100%"
        height={CHART_HEIGHT}
        viewBox={`0 0 ${VIEW_WIDTH} ${CHART_HEIGHT}`}
        preserveAspectRatio="none"
        aria-label={t("dashboard.overview.chart_aria")}
      >
        <path d={buildAreaPath(points)} fill="rgba(15, 118, 110, 0.09)" />
        <path
          d={buildPath(points)}
          fill="none"
          stroke="var(--color-ai)"
          strokeWidth="1.75"
          vectorEffect="non-scaling-stroke"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {winnerPoints.map((w) => (
          <g key={w.experiment_id}>
            <line
              x1={w.x}
              y1={w.y}
              x2={w.x}
              y2={CHART_HEIGHT - BOTTOM_PAD + 2}
              stroke="var(--color-winner)"
              strokeWidth="1"
              strokeDasharray="2,3"
              opacity="0.4"
              vectorEffect="non-scaling-stroke"
            />
            <circle cx={w.x} cy={w.y} r="8" fill="var(--color-winner-dot)" opacity="0.22" />
            <circle cx={w.x} cy={w.y} r="4" fill="var(--color-winner-dot)" />
          </g>
        ))}

        <circle cx={current.x} cy={current.y} r="9" fill="var(--color-ai)" opacity="0.22" />
        <circle cx={current.x} cy={current.y} r="4" fill="var(--color-ai)" />
      </svg>

      {winnerPoints.length > 0 && (
        <div className="relative" style={{ height: 14, marginTop: -CHART_HEIGHT - 2 }}>
          {winnerPoints.map((w) => (
            <span
              key={`label-${w.experiment_id}`}
              className="absolute font-medium text-winner"
              style={{
                left: `${(w.x / VIEW_WIDTH) * 100}%`,
                transform: "translateX(-100%) translateY(-2px)",
                fontSize: 10,
                fontVariantNumeric: "tabular-nums",
                whiteSpace: "nowrap"
              }}
            >
              {t("dashboard.overview.chart_winner_label", {
                sign: w.improvement_pp >= 0 ? "+" : "−",
                value: Math.abs(w.improvement_pp).toFixed(1)
              })}
            </span>
          ))}
        </div>
      )}

      <div
        className="grid mt-3 font-mono text-[10px] text-ink-faint"
        style={{ gridTemplateColumns: "repeat(5, 1fr)", letterSpacing: "0.08em" }}
      >
        {axisIndices.map((i, pos) => (
          <span
            key={pos}
            style={{ textAlign: pos === 0 ? "left" : pos === 4 ? "right" : "center" }}
          >
            {series[i] ? monthDay(series[i].date) : ""}
          </span>
        ))}
      </div>
    </div>
  )
}
